import type { ButtonProps } from "@mui/material";
import { Button, Tooltip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { createTheme, styled } from "@mui/material/styles";
import { ROLES } from "containers/ScopeContext/roles.enum";
import useUserRole from "hooks/useUserRole";
import { Fragment } from "react";

import colors from "../../../assets/styles/_variables.module.scss";
import variables from "../../../assets/styles/_variables.module.scss";

interface ResponsiveButtonProps extends ButtonProps {
    buttonType?: "dark" | "white" | "outlined";
    tooltipText?: string;
}

const RButton = styled((props: ResponsiveButtonProps & { loading?: boolean }) => <Button {...props} />)(
    ({ theme, disabled }) => ({
        "&.MuiButton-root": {
            // backgroundColor: "white",
            border: `1px solid ${colors["border-grey"]}`,
            color: "black",
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            fontWeight: "400",
            padding: "6px 18px",
            height: "1.9rem",
            width: "100%",
            borderRadius: variables["border-radius"],
            gap: "8px",
            "&:hover": {
                backgroundColor: "#eaeaea",
            },
            ...(disabled
                ? {
                      backgroundColor: "#eaeaea !important",
                      color: "#00000040 !important",
                  }
                : {}),
            [theme.breakpoints.down(1300)]: {
                width: "35px",
                minWidth: "35px",
                height: "35px",

                svg: {
                    position: "absolute",
                },
            },
        },
        "> span": {
            whiteSpace: "nowrap",

            [theme.breakpoints.down(1300)]: {
                display: "none",
            },
        },
    }),
);

const blackButtonStyles = {
    "&.MuiButton-root": {
        backgroundColor: "#303030",
        border: "none",
        color: "white",
        "&:hover": {
            backgroundColor: "#464646",
        },
    },
};
const whiteButtonStyles = {
    "&.MuiButton-root": {
        color: "black",
        backgroundColor: "white",
        border: `1px solid ${variables["border-grey"]}`,
        borderRadius: variables["border-radius"],
    },
};

const outlinedWhiteButtonStyles = {
    border: "1px solid black",
};

const ResponsiveButton = ({ buttonType, sx = {}, tooltipText = "", ...props }: ResponsiveButtonProps) => {
    const { userRole } = useUserRole();

    if (userRole === ROLES.VIEW_ONLY) return <></>;

    return (
        <Tooltip title={tooltipText} arrow>
            <div>
                <RButton
                    {...props}
                    sx={mergeSx(
                        buttonType === "dark"
                            ? blackButtonStyles
                            : buttonType === "white"
                              ? whiteButtonStyles
                              : buttonType === "outlined"
                                ? outlinedWhiteButtonStyles
                                : {},
                        sx,
                    )}
                />
            </div>
        </Tooltip>
    );
};

export default ResponsiveButton;

export const mergeSx = (...sxProps: SxProps<Theme>[]): SxProps<Theme> => {
    return sxProps.reduce((prev, currentValue) => {
        return [
            ...(Array.isArray(prev) ? prev : [prev]),
            ...(Array.isArray(currentValue) ? currentValue : [currentValue]),
        ];
    }, [] as SxProps<Theme>);
};
